import { fetchUrl } from './files';
import { api } from './index.js';

/**
 * Where the 3D view's files come from.
 *
 * Production: every file lives in S3 under `{projectUuid}/3d/` and is reached through the file
 * service's signed URLs (`fileLink?key=`). Signed URLs last an hour, and the 3D view keeps fetching
 * pages and tiles for as long as it is open, so URLs are cached here and re-signed before they expire.
 *
 * Development: with `VITE_APP_VIEW3D_FIXTURE_BASE` set (see .env.template) the files are read
 * straight from that base URL, e.g. a local server laid out by S3 key.
 */
const FIXTURE_BASE = import.meta.env.VITE_APP_VIEW3D_FIXTURE_BASE || null;

/** Signed URLs are valid for 3600 s; refresh them well before that. */
const SIGNED_URL_TTL_MS = 50 * 60 * 1000;

/** key -> { url, signedAt } or a pending promise */
const signedUrls = new Map();

/** The S3 key of a project's 3D view manifest. */
export const view3dManifestKey = projectUuid => `${projectUuid}/3d/manifest.json`;

export const isView3dFixture = () => !!FIXTURE_BASE;

/** Forgets every cached URL (tests, logout). */
export const clearSignedUrlCache = () => signedUrls.clear();

const signKey = async (key, signal) => {
	const query = new URLSearchParams({ key });
	const data = await fetchUrl(`fileLink?${query.toString()}`, signal);
	// fetchUrl answers {data: null, error} for a missing file rather than throwing
	if (!data?.url) {
		const error = new Error(`No URL for ${key}`);
		error.status = data?.error?.status ?? 404;
		error.key = key;
		throw error;
	}
	return data.url;
};

/**
 * Resolves an S3 key to a URL the browser can fetch.
 *
 * @param {string} key - S3 key, e.g. `{uuid}/3d/manifest.json`
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @param {boolean} [options.fresh] - Ignore the cache (after a 403 on an expired URL)
 * @returns {Promise<string>}
 */
export const getSignedUrl = async (key, { signal, fresh = false } = {}) => {
	if (FIXTURE_BASE) return FIXTURE_BASE.replace(/\/?$/, '/') + key;

	const cached = signedUrls.get(key);
	if (cached && !fresh) {
		if (cached.promise) return cached.promise;
		if (Date.now() - cached.signedAt < SIGNED_URL_TTL_MS) return cached.url;
	}

	const promise = signKey(key, signal)
		.then(url => {
			signedUrls.set(key, { url, signedAt: Date.now() });
			return url;
		})
		.catch(err => {
			signedUrls.delete(key);
			throw err;
		});
	signedUrls.set(key, { promise });
	return promise;
};

/** True when the URL of `key` was signed long enough ago that it should be refreshed. */
export const isSignedUrlStale = key => {
	if (FIXTURE_BASE) return false;
	const cached = signedUrls.get(key);
	return !cached || (!cached.promise && Date.now() - cached.signedAt >= SIGNED_URL_TTL_MS);
};

/**
 * Loads the project's 3D view manifest, or resolves `null` when the project has none.
 * A missing manifest is the normal case for most projects, so it is one request, no retries.
 *
 * @param {string} projectUuid
 * @param {string} [manifestKey] - From `project.view3d.manifest_path` when the backend provides it
 */
export const getView3dManifest = async (projectUuid, manifestKey) => {
	const key = manifestKey || view3dManifestKey(projectUuid);
	let url;
	try {
		url = await getSignedUrl(key, { fresh: true });
	} catch (err) {
		if (err.status === 404 || err.status === 403 || err.status === 204) return null;
		throw err;
	}
	const response = await fetch(url, { cache: 'no-cache' });
	if (response.status === 404 || response.status === 403) return null;
	if (!response.ok) throw new Error(`Manifest ${key}: ${response.status}`);
	const manifest = await response.json();
	if (manifest?.version !== 1 || !manifest.tiers) {
		throw new Error(`Manifest ${key}: unsupported format`);
	}
	return manifest;
};

/**
 * Pseudo-origin for 3D Tiles. The tile renderer resolves relative tile URIs against the tileset
 * URL, so the tileset is loaded from `{VIEW3D_TILES_ORIGIN}{key}` and every request is turned
 * back into an S3 key and signed in `keyFromTilesUrl`.
 */
export const VIEW3D_TILES_ORIGIN = 'https://view3d.local/';

export const tilesUrlFromKey = key => VIEW3D_TILES_ORIGIN + key;

export const keyFromTilesUrl = url => {
	if (!url.startsWith(VIEW3D_TILES_ORIGIN)) return null;
	return url.slice(VIEW3D_TILES_ORIGIN.length).split(/[?#]/)[0];
};

/**
 * Fetches a 3D tile (or any project file) by its pseudo-URL or S3 key, re-signing once on 403.
 * @param {string} url - `{VIEW3D_TILES_ORIGIN}{key}` or a plain key
 * @param {RequestInit} [options]
 */
export const fetchProjectFile = async (url, options = {}) => {
	const key = keyFromTilesUrl(url) ?? url;
	const signed = await getSignedUrl(key, { signal: options.signal });
	let response = await fetch(signed, options);
	if (response.status === 403 && !FIXTURE_BASE) {
		const refreshed = await getSignedUrl(key, { signal: options.signal, fresh: true });
		response = await fetch(refreshed, options);
	}
	return response;
};

/**
 * Starts a 3D-view build of a quality the project does not have yet; it is added next to the ones already built.
 * @param {string} projectUuid
 * @param {'preview'|'standard'|'high'} quality
 * @returns {Promise<object>} `{ job_uuid, view3d }`
 */
export const requestView3dBuild = async (projectUuid, quality) => {
	const { data } = await api.post(`/photogrammetry/view3d/${projectUuid}`, null, {
		params: { quality },
	});
	return data;
};
